import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Radio, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData.nome, formData.email, formData.password);
      
      if (result.approved) {
        // First user - auto approved as admin
        toast.success('Registrazione completata! Sei stato impostato come amministratore.');
        navigate('/dashboard');
      } else {
        // Normal user - needs approval
        setRegistrationComplete(true);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la registrazione';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <div className="min-h-screen login-background">
        <div className="min-h-screen login-overlay flex items-center justify-center p-4">
          <div className="w-full max-w-md animate-fade-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="p-3 bg-primary rounded-xl">
                  <Radio className="h-8 w-8 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
                RadioManager Pro
              </h1>
            </div>

            <Card className="border-0 shadow-2xl">
              <CardContent className="pt-6">
                <Alert className="bg-amber-50 border-amber-200">
                  <CheckCircle2 className="h-5 w-5 text-amber-600" />
                  <AlertTitle className="text-amber-800 font-semibold">Registrazione completata</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    Il tuo account è stato creato con successo.
                    <br /><br />
                    <strong>È richiesta l'approvazione dell'amministratore</strong> prima di poter accedere al sistema.
                    <br /><br />
                    Riceverai una notifica quando il tuo account sarà attivato.
                  </AlertDescription>
                </Alert>

                <div className="mt-6 text-center">
                  <Link to="/login">
                    <Button variant="outline" className="w-full">
                      Torna al Login
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen login-background">
      <div className="min-h-screen login-overlay flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary rounded-xl">
                <Radio className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
              RadioManager Pro
            </h1>
            <p className="text-slate-300 mt-2">
              Gestione Campagne Pubblicitarie Radiofoniche
            </p>
          </div>

          <Card className="border-0 shadow-2xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-['Manrope']">Crea Account</CardTitle>
              <CardDescription>
                Inserisci i tuoi dati per registrarti
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome completo</Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    placeholder="Mario Rossi"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    data-testid="register-nome-input"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="nome@esempio.it"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                    data-testid="register-email-input"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimo 6 caratteri"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="register-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Conferma Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Ripeti la password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="register-confirm-password-input"
                    className="h-11"
                  />
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-700 text-sm">
                    Nota: La registrazione richiede l'approvazione dell'amministratore prima di poter accedere.
                  </AlertDescription>
                </Alert>

                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Registrazione in corso...
                    </div>
                  ) : (
                    'Registrati'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-slate-500">Hai già un account?</span>{' '}
                <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                  Accedi
                </Link>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-slate-400 text-sm mt-6">
            &copy; 2024 RadioManager Pro. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
